import React from 'react';
import { Stack } from '@mui/material';
import {
  EMPLOYEE_NOTIFICATIONS,
  MANAGER_NOTIFICATIONS,
  NOTIFICATION_DETAILS,
} from '../../constants/notifications';
import { getChannelsString } from '../../helpers/notifications';
import useUser from '../../hooks/global/useUser';
import useGetNotificationPreferences from '../../hooks/notifications/useGetNotificationPreferences';
import useUpdateNotificationPreferences from '../../hooks/notifications/useUpdateNotificationPreferences';
import {
  NotificationChannel,
  NotificationChannels,
  NotificationPreference,
  NotificationType,
} from '../../types/notifications';
import SettingsSection from '../businesses/SettingsSection';
import MultiSwitchInput from '../form/base/MultiSwitchInput';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import ColoredAlert from '../global/ColoredAlert';

interface Props {
  type: 'employee' | 'manager';
}

function NotificationsSettingsTab({ type }: Props) {
  const { user } = useUser();
  const businessId = type === 'manager' ? user?.business?.id : undefined;
  const notificationTypes =
    type === 'employee' ? EMPLOYEE_NOTIFICATIONS : MANAGER_NOTIFICATIONS;

  const { data, isLoading, refetch } = useGetNotificationPreferences({
    businessId,
  });

  const [currentForm, setCurrentForm] = React.useState<FormData<{
    channels: NotificationChannel[];
  }> | null>(null);

  const { mutateAsync: updateNotificationPreferences } =
    useUpdateNotificationPreferences({
      onSuccess: () => {
        refetch();
        setCurrentForm(null);
      },
    });

  const getForm = React.useCallback(
    (notification: NotificationType) => {
      const { label, description } =
        NOTIFICATION_DETAILS[notification as keyof typeof NOTIFICATION_DETAILS];
      const preferences = (data?.[notification as keyof typeof data] ||
        {}) as NotificationPreference;

      const options: React.ComponentProps<typeof MultiSwitchInput>['options'] =
        [
          {
            label: 'SMS',
            value: NotificationChannels.SMS,
          },
          {
            label: 'Email',
            value: NotificationChannels.EMAIL,
          },
        ];

      if (type === 'employee') {
        options.push({
          label: 'Push',
          value: NotificationChannels.PUSH,
        });
      }

      return {
        initialValues: {
          channels: preferences?.channels || [],
        },
        onSubmit: async values => {
          await updateNotificationPreferences({
            businessId,
            preferences: [
              {
                type: notification,
                channels: values.channels,
              },
            ],
          });
        },
        inputRows: [
          [
            {
              inputType: 'multi-switch',
              label,
              description,
              name: 'channels',
              gap: 4,
              options,
            },
          ],
        ],
        submitButtonText: 'Save',
      } as NonNullable<typeof currentForm>;
    },
    [businessId, data, type, updateNotificationPreferences],
  );

  const onEdit = React.useCallback(
    (notification: NotificationType) => {
      const form = getForm(notification);
      if (!form) return;
      setCurrentForm(form);
    },
    [getForm],
  );

  return (
    <Stack gap={3} mt={3}>
      <ColoredAlert
        persistentDismissAlertKey="notifications-settings-tab-info"
        type="info"
        title="These notification settings only apply to you!"
        subtitle="These are your individual notification settings. Update your personal preferences below. All users can update their own notification settings by accessing this page, or through the Notifications page on the Profile in their mobile app."
        animate={false}
      />

      <SettingsSection
        isLoading={isLoading}
        rows={notificationTypes.map(notification => {
          const { label } =
            NOTIFICATION_DETAILS[
              notification as keyof typeof NOTIFICATION_DETAILS
            ];
          const preferences = (data?.[notification] ||
            {}) as NotificationPreference;

          const channelsString = getChannelsString(preferences?.channels || []);
          const isEnabled =
            preferences?.enabled && preferences?.channels?.length > 0;

          return {
            id: notification,
            title: label,
            label: isEnabled ? `On: ${channelsString}` : `Off`,
            action: {
              type: 'button',
              text: 'Edit',
              onClick: () => onEdit(notification),
            },
          };
        })}
        title="Notifications"
        subtitle="Configure text, email, and push notifications through Porter"
      />
      {currentForm && (
        <FormSmallModal
          form={currentForm}
          onClose={() => setCurrentForm(null)}
          open={!!currentForm}
          allowUntouched={false}
        />
      )}
    </Stack>
  );
}

export default NotificationsSettingsTab;
